import * as React from 'react'

/**
 * Subscribes to a CSS media query and returns whether it currently matches.
 *
 * Safe to call during SSR or in tests where `window.matchMedia` is not
 * available — the hook returns `false` in those environments and the
 * subscription is established lazily on the client. Used to drive
 * viewport-aware UX such as auto-collapsing the chat sidebar on smaller
 * screens or switching it from a docked grid column to a floating overlay.
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = React.useCallback((): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false
    }
    return window.matchMedia(query).matches
  }, [query])

  const [matches, setMatches] = React.useState<boolean>(getMatch)

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }
    const mql = window.matchMedia(query)
    const handler = (event: MediaQueryListEvent): void => {
      setMatches(event.matches)
    }
    setMatches(mql.matches)
    mql.addEventListener('change', handler)
    return () => {
      mql.removeEventListener('change', handler)
    }
  }, [query])

  return matches
}

// Tailwind v4 default breakpoints — keep in sync with `tailwindcss`.
export const BREAKPOINT_QUERIES = {
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)'
} as const

export default useMediaQuery
